import { Loader2, X, Scissors, Square, CheckCircle2, MinusCircle } from "lucide-react";

const CHECKER =
  "repeating-conic-gradient(#1c1c20 0% 25%, #26262c 0% 50%) 50% / 18px 18px";

export const CutoutPreview = ({
  product,
  previews,
  isLoading,
  imageCount,
  mode,
  onModeChange,
  onClose,
  onDownload,
  isDownloading
}) => {
  const processed = previews.filter((p) => p.processed).length;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6"
      data-testid="cutout-preview-modal"
    >
      <div className="bg-[#0B0B0D] border border-[#27272A] w-full max-w-6xl my-8">
        <div className="flex items-start justify-between gap-6 border-b border-[#27272A] p-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#00FFFF] mb-2">
              Cut-out preview
            </p>
            <h2 className="text-lg text-white">{product.title}</h2>
            <p className="text-sm text-[#71717A] mt-1" data-testid="preview-summary">
              {isLoading
                ? "Processing previews..."
                : `${processed} of ${previews.length} images will be ${mode === "white" ? "placed on white" : "cut out"} — the rest download untouched`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[#71717A] hover:text-white transition-colors"
            data-testid="preview-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-b border-[#27272A] px-6 py-4">
          <button
            onClick={() => onModeChange("transparent")}
            className={`text-[10px] uppercase tracking-wider px-4 py-2 border transition-all flex items-center gap-2 ${
              mode === "transparent"
                ? "border-[#FF00CC] bg-[#FF00CC]/15 text-[#FF8AE2]"
                : "border-[#333333] text-[#71717A] hover:text-white"
            }`}
            data-testid="preview-mode-transparent"
          >
            <Scissors className="w-3 h-3" /> Transparent
          </button>
          <button
            onClick={() => onModeChange("white")}
            className={`text-[10px] uppercase tracking-wider px-4 py-2 border transition-all flex items-center gap-2 ${
              mode === "white"
                ? "border-white bg-white/15 text-white"
                : "border-[#333333] text-[#71717A] hover:text-white"
            }`}
            data-testid="preview-mode-white"
          >
            <Square className="w-3 h-3" /> White
          </button>
          <div className="ml-auto">
            <button
              onClick={() => onDownload(mode)}
              disabled={isLoading || isDownloading}
              className="bg-[#00FFFF] text-black font-bold text-xs uppercase tracking-wider px-6 py-2.5 hover:bg-[#00FFFF]/80 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="preview-download-btn"
            >
              {isDownloading ? (
                <><Loader2 className="w-3 h-3 animate-spin" /> Preparing...</>
              ) : (
                <>Download this set</>
              )}
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-24 text-[#71717A]">
            <Loader2 className="w-6 h-6 animate-spin text-[#00FFFF]" />
            <p className="text-sm">Cutting out {imageCount ?? product.images?.length ?? 0} images at preview quality...</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-6">
            {previews.map((preview) => (
              <div key={preview.index} data-testid={`preview-tile-${preview.index}`}>
                <div
                  className="aspect-square border border-[#27272A] flex items-center justify-center overflow-hidden"
                  style={{
                    background: preview.processed
                      ? (mode === "white" ? "#F4F4F5" : CHECKER)
                      : "#101013"
                  }}
                  title={`Image ${String(preview.index).padStart(2, "0")}${preview.error ? " — could not be fetched" : ""}`}
                >
                  {preview.thumbnail ? (
                    <img
                      src={preview.thumbnail}
                      alt={`Preview ${preview.index}`}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <span className="text-xs text-[#71717A]">Unavailable</span>
                  )}
                </div>
                <div className="flex items-center gap-2 pt-2">
                  {preview.processed ? (
                    <>
                      <CheckCircle2 className="w-3 h-3 text-[#FF8AE2] shrink-0" />
                      <span className="text-[10px] uppercase tracking-wider text-[#FF8AE2]">
                        {mode === "white" ? "On white" : "Cut out"}
                      </span>
                    </>
                  ) : (
                    <>
                      <MinusCircle className="w-3 h-3 text-[#8A8A93] shrink-0" />
                      <span className="text-[10px] uppercase tracking-wider text-[#A1A1AA]">
                        {preview.error ? "Fetch failed" : "Left untouched"}
                      </span>
                    </>
                  )}
                  <span className="ml-auto text-[10px] text-[#8A8A93]">
                    {String(preview.index).padStart(2, "0")}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
